import React, { useState, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  Alert, 
  Paper,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const SuperAdminSetupPage = () => {
  const { setupSuperAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      await setupSuperAdmin(
        formData.email, 
        formData.password, 
        formData.firstName, 
        formData.lastName
      );
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Setup failed');
    }
    setLoading(false);
  };

  return (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      minHeight="100vh"
      bgcolor="background.default"
      p={2}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={4}>
            <AdminPanelSettingsIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h3" gutterBottom>
              Welcome to NeoShift.ai
            </Typography>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Set up your Super Administrator Account
            </Typography>
            <Typography variant="body2" color="textSecondary">
              This is a one-time setup to create the first administrator account for your organization.
            </Typography>
          </Box>

          <Stepper activeStep={0} sx={{ mb: 4 }}>
            <Step>
              <StepLabel>Super Admin Setup</StepLabel>
            </Step>
            <Step>
              <StepLabel>System Configuration</StepLabel>
            </Step>
            <Step>
              <StepLabel>Team Invitations</StepLabel>
            </Step>
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <Box display="flex" gap={2} mb={2}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                fullWidth
                required
              />
            </Box>

            <TextField
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              fullWidth
              margin="normal"
              required
              helperText="This will be your login email and primary contact"
            />

            <TextField
              label="Password"
              type="password"
              value={formData.password}
              onChange={handleChange('password')}
              fullWidth
              margin="normal"
              required
              helperText="Must be at least 8 characters long"
            />

            <TextField
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              fullWidth
              margin="normal"
              required
              error={formData.confirmPassword && formData.password !== formData.confirmPassword}
              helperText={
                formData.confirmPassword && formData.password !== formData.confirmPassword
                  ? "Passwords do not match"
                  : ""
              }
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || !formData.email || !formData.password || formData.password !== formData.confirmPassword}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? 'Creating Account...' : 'Create Super Admin Account'}
            </Button>
          </form>

          <Paper elevation={0} sx={{ bgcolor: 'info.light', p: 2, mt: 3 }}>
            <Typography variant="body2" color="info.dark">
              <strong>What happens next?</strong><br />
              After creating your account, you'll be able to:
              <br />• Invite team members and assign roles
              <br />• Create and manage companies
              <br />• Set up projects and workflows
              <br />• Configure system settings
            </Typography>
          </Paper>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SuperAdminSetupPage;
